/**
 * Public check-in form — no login required.
 * Clients arrive via a magic link: /checkin/:token
 *
 * Fetches the questionnaire by token, renders questions with appropriate
 * inputs (rating, yes/no, number, freetext), and submits responses.
 */

import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { config } from '../config/environment';

// ─── Types ────────────────────────────────────────────────────────────────────

interface CheckInQuestion {
  id: string;
  text: string;
  type: 'rating' | 'yesno' | 'number' | 'freetext' | 'dropdown_single' | 'dropdown_multi';
  order_index: number;
  options?: string[] | null;
}

interface CheckInData {
  status: 'open' | 'closed';
  questionnaire_id: string;
  title: string;
  description?: string;
  client_first_name?: string;
  message?: string;
  questions: CheckInQuestion[];
}

interface Answer {
  question_id: string;
  rating_value?: number;
  bool_value?: boolean;
  text_value?: string;
}

// ─── Sub-inputs ───────────────────────────────────────────────────────────────

const RatingInput = ({ value, onChange }: { value?: number; onChange: (v: number) => void }) => (
  <div className="flex gap-2 mt-2 flex-wrap">
    {[1, 2, 3, 4, 5].map(n => (
      <button
        key={n}
        type="button"
        onClick={() => onChange(n)}
        className={`w-11 h-11 rounded-full border-2 text-sm font-semibold transition-colors ${
          value === n
            ? 'border-indigo-600 bg-indigo-600 text-white'
            : 'border-gray-300 text-gray-500 hover:border-indigo-400 hover:text-indigo-600'
        }`}
      >
        {n}
      </button>
    ))}
    <span className="self-center text-xs text-gray-400 ml-1">1 = low · 5 = high</span>
  </div>
);

const YesNoInput = ({ value, onChange }: { value?: boolean; onChange: (v: boolean) => void }) => (
  <div className="flex gap-3 mt-2">
    {([true, false] as const).map(v => (
      <button
        key={String(v)}
        type="button"
        onClick={() => onChange(v)}
        className={`px-8 py-2.5 rounded-lg border-2 text-sm font-medium transition-colors ${
          value === v
            ? 'border-indigo-600 bg-indigo-600 text-white'
            : 'border-gray-300 text-gray-600 hover:border-indigo-400'
        }`}
      >
        {v ? 'Yes' : 'No'}
      </button>
    ))}
  </div>
);

const NumberInput = ({ value, onChange }: { value?: number; onChange: (v: number) => void }) => (
  <input
    type="number"
    min={0}
    value={value ?? ''}
    onChange={e => onChange(parseInt(e.target.value, 10) || 0)}
    className="mt-2 w-28 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
    placeholder="0"
  />
);

const FreetextInput = ({ value, onChange }: { value?: string; onChange: (v: string) => void }) => (
  <textarea
    rows={3}
    value={value ?? ''}
    onChange={e => onChange(e.target.value)}
    className="mt-2 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 resize-none"
    placeholder="Type your answer…"
  />
);

const DropdownSingleInput = ({ options, value, onChange }: { options: string[]; value?: string; onChange: (v: string) => void }) => (
  <div className="flex flex-col gap-2 mt-2">
    {options.map(opt => (
      <button
        key={opt}
        type="button"
        onClick={() => onChange(opt)}
        className={`text-left px-4 py-2.5 rounded-lg border-2 text-sm transition-colors ${
          value === opt
            ? 'border-indigo-600 bg-indigo-600 text-white'
            : 'border-gray-200 text-gray-700 hover:border-indigo-300 hover:bg-indigo-50'
        }`}
      >
        {opt}
      </button>
    ))}
  </div>
);

const DropdownMultiInput = ({ options, value, onChange }: { options: string[]; value?: string; onChange: (v: string) => void }) => {
  const selected = value ? value.split('|||') : [];
  function toggle(opt: string) {
    const next = selected.includes(opt)
      ? selected.filter(s => s !== opt)
      : [...selected, opt];
    onChange(next.join('|||'));
  }
  return (
    <div className="flex flex-col gap-2 mt-2">
      {options.map(opt => (
        <button
          key={opt}
          type="button"
          onClick={() => toggle(opt)}
          className={`text-left px-4 py-2.5 rounded-lg border-2 text-sm transition-colors ${
            selected.includes(opt)
              ? 'border-indigo-600 bg-indigo-600 text-white'
              : 'border-gray-200 text-gray-700 hover:border-indigo-300 hover:bg-indigo-50'
          }`}
        >
          {opt}
        </button>
      ))}
      {selected.length > 0 && (
        <p className="text-xs text-indigo-600 mt-1">{selected.length} selected</p>
      )}
    </div>
  );
};

// ─── Main component ───────────────────────────────────────────────────────────

const todayIso = () => new Date().toISOString().split('T')[0];

const CheckInPublicForm: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const [data, setData] = useState<CheckInData | null>(null);
  const [answers, setAnswers] = useState<Record<string, Answer>>({});
  const [responseDate, setResponseDate] = useState<string>(todayIso());
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!token) return;
    fetch(`${config.apiBaseUrl}/checkin/${token}`)
      .then(r => r.json())
      .then(setData)
      .catch(() => setError('Unable to load check-in. The link may be invalid.'))
      .finally(() => setLoading(false));
  }, [token]);

  const setAnswer = (questionId: string, partial: Partial<Answer>) => {
    setAnswers(prev => ({
      ...prev,
      [questionId]: { ...prev[questionId], question_id: questionId, ...partial },
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!data) return;

    const answeredList = Object.values(answers).filter(a =>
      a.rating_value !== undefined || a.bool_value !== undefined || (a.text_value ?? '').trim() !== ''
    );

    setError(null);
    setSubmitting(true);

    const payload = {
      answers: answeredList,
      response_date: responseDate,
    };

    try {
      const res = await fetch(`${config.apiBaseUrl}/checkin/${token}/respond`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        const detail = err.detail;
        const message = typeof detail === 'string'
          ? detail
          : Array.isArray(detail)
            ? detail.map((e: any) => e.msg ?? String(e)).join(', ')
            : 'Submission failed. Please try again.';
        setError(message);
        return;
      }
      setSubmitted(true);
    } catch {
      setError('Network error. Please check your connection and try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // ── Render states ────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error && !data) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
        <div className="text-center max-w-sm">
          <p className="text-gray-500 text-sm">{error}</p>
        </div>
      </div>
    );
  }

  if (data?.status === 'closed') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8 max-w-sm w-full text-center">
          <p className="text-2xl mb-3">✅</p>
          <h1 className="text-lg font-semibold text-gray-800 mb-2">{data.title}</h1>
          <p className="text-sm text-gray-500">{data.message || 'This check-in is no longer active.'}</p>
        </div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8 max-w-sm w-full text-center">
          <p className="text-4xl mb-4">🎉</p>
          <h1 className="text-xl font-semibold text-gray-800 mb-2">
            Thanks{data?.client_first_name ? `, ${data.client_first_name}` : ''}!
          </h1>
          <p className="text-sm text-gray-500">
            Your responses have been recorded. Your coach will review them before your next session.
          </p>
        </div>
      </div>
    );
  }

  if (!data) return null;

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-lg mx-auto">
        {/* Header */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 mb-4">
          <p className="text-xs font-medium text-indigo-600 uppercase tracking-wide mb-1">
            Check-in
          </p>
          <h1 className="text-xl font-semibold text-gray-800">{data.title}</h1>
          {data.client_first_name && (
            <p className="text-sm text-gray-500 mt-1">Hi {data.client_first_name}!</p>
          )}
          {data.description && (
            <p className="text-sm text-gray-500 mt-2">{data.description}</p>
          )}
          <div className="mt-4 pt-4 border-t border-gray-100">
            <label className="block text-xs font-medium text-gray-500 mb-1">
              Response Date
            </label>
            <input
              type="date"
              value={responseDate}
              onChange={e => setResponseDate(e.target.value)}
              max={todayIso()}
              className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>
        </div>

        {/* Questions */}
        <form onSubmit={handleSubmit}>
          <div className="space-y-3 mb-4">
            {data.questions.map((q, idx) => {
              const a = answers[q.id];
              return (
                <div
                  key={q.id}
                  className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5"
                >
                  <p className="text-sm font-medium text-gray-800">
                    <span className="text-indigo-400 font-bold mr-2">{idx + 1}.</span>
                    {q.text}
                  </p>
                  {q.type === 'rating' && (
                    <RatingInput
                      value={a?.rating_value}
                      onChange={v => setAnswer(q.id, { rating_value: v })}
                    />
                  )}
                  {q.type === 'yesno' && (
                    <YesNoInput
                      value={a?.bool_value}
                      onChange={v => setAnswer(q.id, { bool_value: v })}
                    />
                  )}
                  {q.type === 'number' && (
                    <NumberInput
                      value={a?.rating_value}
                      onChange={v => setAnswer(q.id, { rating_value: v })}
                    />
                  )}
                  {q.type === 'freetext' && (
                    <FreetextInput
                      value={a?.text_value}
                      onChange={v => setAnswer(q.id, { text_value: v })}
                    />
                  )}
                  {q.type === 'dropdown_single' && (
                    <DropdownSingleInput
                      options={q.options ?? []}
                      value={a?.text_value}
                      onChange={v => setAnswer(q.id, { text_value: v })}
                    />
                  )}
                  {q.type === 'dropdown_multi' && (
                    <DropdownMultiInput
                      options={q.options ?? []}
                      value={a?.text_value}
                      onChange={v => setAnswer(q.id, { text_value: v })}
                    />
                  )}
                </div>
              );
            })}
          </div>

          {error && (
            <p className="text-sm text-red-500 mb-3 px-1">{error}</p>
          )}

          <button
            type="submit"
            disabled={submitting}
            className="w-full bg-indigo-600 text-white rounded-xl py-3 font-medium text-sm hover:bg-indigo-700 disabled:opacity-50 transition-colors"
          >
            {submitting ? 'Submitting…' : 'Submit Check-in'}
          </button>

          <p className="text-xs text-center text-gray-400 mt-4">
            Powered by CoachSight · Your responses are shared only with your coach
          </p>
        </form>
      </div>
    </div>
  );
};

export default CheckInPublicForm;
